import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import Icon from '@projeccions-pdc/react-icon';

const styles = theme => ({
    root: {
        flexGrow: 1,
    },
    menu: {
        zIndex: 2,
    },
    input: {
        display: 'flex',
        padding: 0,
        height: 'auto',
    },
    valueContainer: {
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    chip: {
        margin: `${theme.spacing(1) / 2}px ${theme.spacing(1) / 4}px`
    },
    chipFocused: {
        backgroundColor: emphasize(
            theme.palette.type === 'light'
                ? theme.palette.grey[300]
                : theme.palette.grey[700],
            0.08
        )
    },
    noOptionsMessage: {
        fontSize: 16,
        padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`
    },
    singleValue: {
        fontSize: 16
    },
    placeholder: {
        position: 'absolute',
        left: 2,
        fontSize: 16
    },
    underlineLight: {
        borderBottom: '2px solid #fff!important',
        fontSize: '20px',
        color: '#fff',
        '&:after': {
            borderBottom: 'none!important'
        },
        '&:before': {
            borderBottom: 'none!important'
        },
    },
    underlineDark: {
        borderBottom: '2px solid #979797!important',
        fontSize: '20px',
        color: '#000',
        '&:after': {
            borderBottom: 'none!important'
        },
        '&:before': {
            borderBottom: 'none!important'
        },
    },
});


let NoOptionsMessage = (props) => {
    //console.log('NoOptionsMessage')
    return (
        <Typography
            color='textSecondary'
            className={props.selectProps.classes.noOptionsMessage}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
};
NoOptionsMessage.propTypes = {
    selectProps: PropTypes.any,
    children: PropTypes.any,
    innerProps: PropTypes.any,
};

let inputComponent = ({ inputRef, ...props }) => {
    //console.log('inputComponent')
    return <div ref={inputRef} {...props} />;
};
inputComponent.propTypes = {
    inputRef: PropTypes.any,
};

let Control = (props) => {
    // console.log('Control', props)
    return (
        <TextField
            data-id="search-field-pdc"
            autoFocus={true}
            fullWidth
            InputProps={{
                inputComponent,
                inputProps: {
                    className: props.selectProps.classes.input,
                    ref: props.innerRef,
                    children: props.children,
                    ...props.innerProps
                },
                classes: {
                    underline: props.selectProps.classes.underline,
                }
            }}
        />
    );
};
Control.propTypes = {
    selectProps: PropTypes.any,
    children: PropTypes.any,
    innerRef: PropTypes.any,
    innerProps: PropTypes.any,
};

let Option = (props) => {
    //console.log('Option')
    return (
        <MenuItem
            buttonRef={props.innerRef}
            selected={props.isFocused}
            component='div'
            style={{
                fontWeight: props.isSelected ? 500 : 400,
                color: '#000',
            }}
            {...props.innerProps}
        >
            {props.children}
        </MenuItem>
    );
};
Option.propTypes = {
    selectProps: PropTypes.any,
    children: PropTypes.any,
    innerRef: PropTypes.any,
    isFocused: PropTypes.any,
    isSelected: PropTypes.any,
    innerProps: PropTypes.any,
};

let Placeholder = (props) => {
    // console.log('Placeholder', props)
    return (
        <Typography
            // color={props.theme.colors.neutral50}//'textSecondary'
            className={props.selectProps.classes.placeholder}
            {...props.innerProps}
            style={{ color: props.theme.colors.neutral70 }}
        >
            {props.children}
        </Typography>
    );
};
Placeholder.propTypes = {
    selectProps: PropTypes.any,
    children: PropTypes.any,
    innerProps: PropTypes.any,
};

let SingleValue = (props) => {
    //console.log('SingleValue')
    return (
        <Typography
            className={props.selectProps.classes.singleValue}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
};
SingleValue.propTypes = {
    selectProps: PropTypes.any,
    children: PropTypes.any,
    innerProps: PropTypes.any,
};

let ValueContainer = (props) => {
    //console.log('ValueContainer')
    return (
        <div className={props.selectProps.classes.valueContainer}>
            {props.children}
        </div>
    );
};
ValueContainer.propTypes = {
    selectProps: PropTypes.any,
    children: PropTypes.any,
};

const CancelIcon = (props) => (
    <div {...props}>
        <Icon name='md-cancel' size='24px' primaryColor={'rgba(0, 0, 0, 0.26)'} />
    </div>
);
let MultiValue = (props) => {
    return (
        <Chip
            tabIndex={-1}
            label={props.children}
            className={classNames(props.selectProps.classes.chip, {
                [props.selectProps.classes.chipFocused]: props.isFocused
            })}
            onDelete={props.removeProps.onClick}
            deleteIcon={<CancelIcon {...props.removeProps} />}
        />
    );
};
MultiValue.propTypes = {
    selectProps: PropTypes.any,
    children: PropTypes.any,
    removeProps: PropTypes.any,
    isFocused: PropTypes.any,
};

const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
};
const groupBadgeStyles = {
    backgroundColor: '#EBECF0',
    borderRadius: '2em',
    color: '#172B4D',
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: '1',
    minWidth: 1,
    padding: '0.16666666666667em 0.5em',
    textAlign: 'center'
};

const listStyle = () => ({
    top: '100%',
    backgroundColor: 'hsl(0,0%,100%)',
    borderRadius: '4px',
    boxShadow: '0 0 0 1px hsla(0,0%,0%,0.1), 0 4px 11px hsla(0,0%,0%,0.1)',
    marginBottom: '8px',
    marginTop: '8px',
    position: 'absolute',
    width: '100%',
    zIndex: 2,
    boxSizing: 'border-box',
});

const formatGroupLabel = data => (
    <div style={groupStyles}>

        <span>{data.label}</span>
        <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
);

const components = {
    Option,
    Control,
    NoOptionsMessage,
    Placeholder,
    SingleValue,
    MultiValue,
    ValueContainer
};

class NewSearch extends React.Component {
    state = {
        single: null,
        multi: null
    };

    static getDerivedStateFromProps(props, state) {
        return { multi: props.activeFilters ? props.activeFilters : undefined };
    }

    handleChange = value => {
        this.setState({
            multi: value
        });
        this.props.onResultSelect(value);
    };

    render() {
        const { classes, usePortal } = this.props;

        let groupedOptions = [];
        let keys = ['firstResults', 'secondResults', 'thirdResults', 'fourthResults', 'fifthResults'];
        keys.forEach((key) => {
            let results = this.props[key];
            if (results) {
                groupedOptions.push({
                    label: results.name,
                    options: results.results
                });
            }
        });

        let style = {
            menu: listStyle
        };
        if (usePortal === true) {
            style.menuPortal = base => ({ ...base, zIndex: 9999 });
        }

        let lightColors = {
            neutral0: 'hsl(0, 0%, 90%)',
            neutral5: 'hsl(0, 0%, 90%)',
            neutral10: 'hsl(0, 0%, 90%)',
            neutral20: 'hsl(0, 0%, 90%)',
            neutral30: 'hsl(0, 0%, 90%)',
            neutral40: 'hsl(0, 0%, 90%)',
            neutral50: 'hsl(0, 0%, 90%)',
            neutral60: 'hsl(0, 0%, 90%)',
            neutral70: 'hsl(0, 0%, 90%)',
            neutral80: 'hsl(0, 0%, 90%)',
            neutral90: 'hsl(0, 0%, 90%)',
        };

        return (
            <div className={classes.root}>
                <Select
                    isDisabled={this.props.disabled}
                    autoFocus={this.props.autoFocus !== undefined ? this.props.autoFocus : true}
                    onFocus={this.props.onFocus}
                    onBlur={this.props.onBlur}
                    onKeyDown={this.props.onKeyDown}
                    ref={this.props.takeRef}
                    isLoading={this.props.isLoading}
                    menuPortalTarget={usePortal === true ? document.body : undefined}
                    menuPlacement={usePortal === true ? 'bottom' : undefined}
                    menuPosition={usePortal === true ? 'absolute' : undefined}
                    classes={{
                        ...classes,
                        underline: (this.props.theme === 'light' ? this.props.classes.underlineLight : undefined),
                    }}
                    styles={style}
                    options={groupedOptions}
                    components={components}
                    inputValue={this.props.value}
                    onInputChange={this.props.onSearchChange}
                    value={this.state.multi}
                    onChange={this.handleChange}
                    placeholder={this.props.placeholder}
                    formatGroupLabel={formatGroupLabel}
                    isMulti={this.props.isMulti}
                    theme={(theme) => ({
                        ...theme,
                        colors: {
                            ...theme.colors,
                            ...(this.props.theme === 'light' ? lightColors : {})
                        },
                    })}
                    filterOption={(option, rawInput) => {
                        return true;
                    }}
                />
            </div>
        );
    }
}

NewSearch.propTypes = {
    autoFocus: PropTypes.bool,
    disabled: PropTypes.bool,
    usePortal: PropTypes.bool,
    classes: PropTypes.object.isRequired,
    firstResults: PropTypes.object,
    secondResults: PropTypes.object,
    thirdResults: PropTypes.object,
    fourthResults: PropTypes.object,
    fifthResults: PropTypes.object,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    isMulti: PropTypes.bool,
    value: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    onResultSelect: PropTypes.func.isRequired,
    activeFilters: PropTypes.any,
    takeRef: PropTypes.func,
    theme: PropTypes.string,
};

export default withStyles(styles)(NewSearch);
